// Soldier ROLES — the little-people come in variants beyond the melee brawler. Each role has a
// combat behavior (melee, or a ranged weapon firing into the shared projectile system) and a
// simple held-weapon prop (a scaled/tinted box) so you can tell a bazooka from a rifleman.

export type Attack = "melee" | "bullet" | "rocket" | "arc";

/** Held-weapon visual (a box scaled by fractions of the soldier's height, tinted, tilted up). */
export interface WeaponViz {
  sx: number; // width  (× height)
  sy: number; // height (× height)
  sz: number; // length (× height)
  color: number;
  pitch: number; // barrel tilt up (rad)
}

export interface SoldierRole {
  id: string;
  weight: number; // spawn weight
  attack: Attack;
  range: number; // engage range (studs) — acquire + the distance it holds to fire
  interval: number; // seconds between attacks
  damage: number;
  needsLOS: boolean; // bullet/rocket need a clear line; arc (grenade/mortar) lobs over cover
  speed: number; // projectile speed (studs/s) for bullet/rocket; unused by melee/arc
  bounces: number;
  blastR: number; // 0 => single hit; >0 => AoE blast
  projSize: number; // shell render scale
  arcPeak: number; // lob apex (studs) for arc
  weapon: WeaponViz | null; // held prop, or none (melee / grenadier throw)
  cost: number; // Unit Points to spawn — the plain melee grunt is 0 (free filler); placeholder values
}

const rifle: WeaponViz = { sx: 0.1, sy: 0.1, sz: 0.95, color: 0x5a5f66, pitch: 0 };
const mg: WeaponViz = { sx: 0.15, sy: 0.15, sz: 1.15, color: 0x33383e, pitch: 0 };
const tube: WeaponViz = { sx: 0.2, sy: 0.2, sz: 1.05, color: 0x556040, pitch: 0.05 };
const mortarTube: WeaponViz = { sx: 0.17, sy: 0.17, sz: 0.75, color: 0x222428, pitch: 0.95 };

// speeds/damage tuned for the 2x stud world (little-person hp 100, tanks 40-480).
export const ROLES: SoldierRole[] = [
  { id: "melee", weight: 38, attack: "melee", range: 300, interval: 0.85, damage: 26, needsLOS: false, speed: 0, bounces: 0, blastR: 0, projSize: 1, arcPeak: 0, weapon: null, cost: 0 },
  { id: "rifleman", weight: 22, attack: "bullet", range: 680, interval: 1.4, damage: 55, needsLOS: true, speed: 720, bounces: 0, blastR: 0, projSize: 0.7, arcPeak: 0, weapon: rifle, cost: 12 },
  { id: "machinegunner", weight: 13, attack: "bullet", range: 560, interval: 0.13, damage: 26, needsLOS: true, speed: 680, bounces: 0, blastR: 0, projSize: 0.6, arcPeak: 0, weapon: mg, cost: 22 },
  { id: "bazooka", weight: 9, attack: "rocket", range: 880, interval: 3.2, damage: 100, needsLOS: true, speed: 520, bounces: 0, blastR: 95, projSize: 1.1, arcPeak: 0, weapon: tube, cost: 38 },
  { id: "grenadier", weight: 8, attack: "arc", range: 460, interval: 2.4, damage: 85, needsLOS: false, speed: 0, bounces: 0, blastR: 100, projSize: 1.1, arcPeak: 150, weapon: null, cost: 30 },
  { id: "mortarman", weight: 7, attack: "arc", range: 1150, interval: 3.8, damage: 100, needsLOS: false, speed: 0, bounces: 0, blastR: 135, projSize: 1.3, arcPeak: 320, weapon: mortarTube, cost: 42 },
];

export const MELEE_ROLE = ROLES[0];

const RANGED = ROLES.filter((r) => r.id !== "melee");
const RANGED_WEIGHT = RANGED.reduce((s, r) => s + r.weight, 0);

/** Weighted-random role for a freshly spawned soldier. `specialFrac` (0..1) is the chance it's a
 *  ranged specialist (weighted among the ranged roles); otherwise it's a melee brawler. */
export function pickRole(specialFrac: number): SoldierRole {
  if (Math.random() >= specialFrac) return MELEE_ROLE;
  let r = Math.random() * RANGED_WEIGHT;
  for (const role of RANGED) {
    r -= role.weight;
    if (r <= 0) return role;
  }
  return RANGED[0];
}
